import { ConfigThemeEntity, IConfigThemeEntityAttributes } from './Entities';
import { stringNotEmpty } from '../Util/TypeGuards';
import { action, observable, makeObservable, computed } from 'mobx';
import {
	FAVICON_URL,
	FEATURE_IMAGE_1_URL,
	FEATURE_IMAGE_2_URL,
	FEATURE_IMAGE_3_URL,
	FEATURE_IMAGE_4_URL,
	FEATURE_IMAGE_5_URL,
	FEATURE_IMAGE_6_URL,
	FEATURE_IMAGE_7_URL,
	IMAGE_API_URL,
	LOGO_PRIMARY_DARK,
	LOGO_PRIMARY_LIGHT,
} from 'Constants';

export interface IThemeStore {
	config: IConfigThemeEntityAttributes | ConfigThemeEntity;
	setTheme: (config: IConfigThemeEntityAttributes | ConfigThemeEntity | undefined) => void;
}

export class ThemeStore implements IThemeStore {
	@observable
	config: IConfigThemeEntityAttributes | ConfigThemeEntity;

	lightTextColour = 'white';
	darkTextColour = '#010c20'; // Same as $color-high-emphasis

	constructor(config: IConfigThemeEntityAttributes[]) {
		makeObservable(this);

		if (!config) {
			return;
		}
		const newConfig = config?.find(x => x.organisation === null);
		if (newConfig) {
			this.config = newConfig;
			updateTheme(newConfig);
		}
	}

	@action
	public setTheme(config: IConfigThemeEntityAttributes | ConfigThemeEntity | undefined) {
		if (config) {
			this.config = config;
			updateTheme(config);
		}
	}

	public get faviconURL() {
		if (this.config?.brandFaviconId) {
			return `${IMAGE_API_URL}${this.config.brandFaviconId}`;
		}
		return FAVICON_URL;
	}

	// ── Per-page background images ──────────────────────────────────────
	// Primary images (required) — one per old FeatureImage group.

	public get homepageBackgroundImageURL() {
		if (this.config?.homepageBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.homepageBackgroundImageId}`;
		}
		return FEATURE_IMAGE_1_URL;
	}

	public get bookingWizardBackgroundImageURL() {
		if (this.config?.bookingWizardBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.bookingWizardBackgroundImageId}`;
		}
		return FEATURE_IMAGE_2_URL;
	}

	public get errorPageBackgroundImageURL() {
		if (this.config?.errorPageBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.errorPageBackgroundImageId}`;
		}
		return FEATURE_IMAGE_4_URL;
	}

	public get bookingSuccessBackgroundImageURL() {
		if (this.config?.bookingSuccessBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.bookingSuccessBackgroundImageId}`;
		}
		return FEATURE_IMAGE_5_URL;
	}

	public get bookingSuccessPromoCardImageURL() {
		if (this.config?.bookingSuccessPromoCardImageId) {
			return `${IMAGE_API_URL}${this.config.bookingSuccessPromoCardImageId}`;
		}
		return FEATURE_IMAGE_7_URL;
	}

	// Optional images — fall back to their primary image if not set.

	public get loginBackgroundImageURL() {
		if (this.config?.loginBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.loginBackgroundImageId}`;
		}
		return this.homepageBackgroundImageURL;
	}

	public get eventBookingWizardBackgroundImageURL() {
		if (this.config?.eventBookingWizardBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.eventBookingWizardBackgroundImageId}`;
		}
		return this.bookingWizardBackgroundImageURL;
	}

	public get bookingCancelSuccessBackgroundImageURL() {
		if (this.config?.bookingCancelSuccessBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.bookingCancelSuccessBackgroundImageId}`;
		}
		return this.bookingSuccessBackgroundImageURL;
	}

	public get eventsBackgroundImageURL() {
		if (this.config?.eventsBackgroundImageId) {
			return `${IMAGE_API_URL}${this.config.eventsBackgroundImageId}`;
		}
		return this.errorPageBackgroundImageURL;
	}

	public get emailHeaderImageURL() {
		if (this.config?.emailHeaderImageId) {
			return `${IMAGE_API_URL}${this.config.emailHeaderImageId}`;
		}
		return this.errorPageBackgroundImageURL;
	}

	public get logoPrimaryDarkURL() {
		if (this.config?.brandLogoPrimaryId) {
			return `${IMAGE_API_URL}${this.config.brandLogoPrimaryId}`;
		}
		return LOGO_PRIMARY_DARK;
	}

	public get logoPrimaryLightURL() {
		if (this.config?.brandLogoPrimaryLightId) {
			return `${IMAGE_API_URL}${this.config.brandLogoPrimaryLightId}`;
		}
		return LOGO_PRIMARY_LIGHT;
	}

	public get logoSecondaryURL() {
		if (this.config?.brandLogoSecondaryId) {
			return `${IMAGE_API_URL}${this.config.brandLogoSecondaryId}`;
		}
		return this.logoPrimaryDarkURL;
	}

	public get logoSecondaryLightURL() {
		if (this.config?.brandLogoSecondaryLightId) {
			return `${IMAGE_API_URL}${this.config.brandLogoSecondaryLightId}`;
		}
		return this.logoPrimaryLightURL;
	}

	public get textColourOnPrimary() {
		return this.config?.textOnPrimaryColour === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}

	public get textColourTypeOnPrimary() {
		return this.config?.textOnPrimaryColour ?? 'LIGHT';
	}

	public get textColourOnSecondary() {
		return this.config?.textOnSecondaryColour === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}

	public get textColourTypeOnSecondary() {
		return this.config?.textOnSecondaryColour ?? 'DARK';
	}

	public get paidTagColour() {
		return stringNotEmpty(this.config?.paidTagColour) ? this.config?.paidTagColour : '#C9E879';
	}

	public get textOnPaidTag() {
		return this.config?.textOnPaidTag === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}

	public get cancelledTagColour() {
		return stringNotEmpty(this.config?.cancelledTagColour) ? this.config?.cancelledTagColour : '#ff6d76';
	}

	public get textOnCancelledTag() {
		return this.config?.textOnCancelledTag === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}

	public get invoicedTagColour() {
		return stringNotEmpty(this.config?.invoicedTagColour) ? this.config?.invoicedTagColour : '#0180b5';
	}

	public get textOnInvoicedTag() {
		return this.config?.textOnInvoicedTag === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}

	public get awaitingTagColour() {
		return stringNotEmpty(this.config?.awaitingTagColour) ? this.config?.awaitingTagColour : '#ffe074';
	}

	public get textOnAwaitingTag() {
		return this.config?.textOnAwaitingTag === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}

	public get newTagColour() {
		return stringNotEmpty(this.config?.newTagColour) ? this.config?.newTagColour : '#0180b5';
	}

	public get textOnNewTag() {
		return this.config?.textOnNewTag === 'LIGHT' ? this.lightTextColour : this.darkTextColour;
	}
}

function updateTheme(themeStore: IConfigThemeEntityAttributes | ConfigThemeEntity) {
	const lightTextColour = 'white';
	const darkTextColour = '#010c20'; // Same as $color-high-emphasis

	const textColourOnPrimary = themeStore?.textOnPrimaryColour === 'LIGHT' ? lightTextColour : darkTextColour;
	const textColourOnSecondary = themeStore?.textOnSecondaryColour === 'LIGHT' ? lightTextColour : darkTextColour;

	const textColourOnNewTag = themeStore?.textOnNewTag === 'LIGHT' ? lightTextColour : darkTextColour;
	const textColourOnPaidTag = themeStore?.textOnPaidTag === 'LIGHT' ? lightTextColour : darkTextColour;
	const textColourOnCancelledTag = themeStore?.textOnCancelledTag === 'LIGHT' ? lightTextColour : darkTextColour;
	const textColourOnInvoicedTag = themeStore?.textOnInvoicedTag === 'LIGHT' ? lightTextColour : darkTextColour;
	const textColourOnAwaitingTag = themeStore?.textOnAwaitingTag === 'LIGHT' ? lightTextColour : darkTextColour;

	const properties: { property: string; value: string }[] = [
		{ property: '--colour-brand-primary', value: themeStore.brandColourPrimary },
		{ property: '--colour-brand-primary-2', value: themeStore.brandColourPrimary2 },
		{ property: '--colour-brand-secondary', value: themeStore.brandColourSecondary },
		{ property: '--colour-brand-secondary-2', value: themeStore.brandColourSecondary2 },
		{ property: '--colour-booking-wizard-backgrounds', value: themeStore.brandColourPrimary },
		{ property: '--text-on-primary', value: textColourOnPrimary },
		{ property: '--text-on-secondary', value: textColourOnSecondary },
		// Trip card colours
		{ property: '--high-availability-trip-card-colour', value: themeStore.highAvailabilityColour || '#C9E879' },
		{ property: '--low-availability-trip-card-colour', value: themeStore.lowAvailabilityColour || '#FFE074' },
		{ property: '--no-availability-trip-card-colour', value: themeStore.noAvailabilityColour || '#9D9D9D' },
		// Tag colours
		{ property: '--paid-tag-colour', value: themeStore.paidTagColour || '#C9E879' },
		{ property: '--text-on-paid-tag', value: textColourOnPaidTag },
		{ property: '--cancelled-tag-colour', value: themeStore.cancelledTagColour || '#FF6D76' },
		{ property: '--text-on-cancelled-tag', value: textColourOnCancelledTag },
		{ property: '--awaiting-tag-colour', value: themeStore.awaitingTagColour || '#FFE074' },
		{ property: '--text-on-awaiting-tag', value: textColourOnAwaitingTag },
		{ property: '--invoiced-tag-colour', value: themeStore.invoicedTagColour || '#0180B5' },
		{ property: '--text-on-invoiced-tag', value: textColourOnInvoicedTag },
		{ property: '--new-tag-colour', value: themeStore.newTagColour || themeStore.brandColourPrimary },
		{ property: '--text-on-new-tag', value: textColourOnNewTag },
	];

	// Apply the properties dynamically
	for (const item of properties) {
		document.documentElement.style.setProperty(item.property, item.value);
	}
}

export const themeStore = new ThemeStore(window.themeData);
